import moment from "moment-timezone";
import { useSolarCalc } from "./calculations";
import { useFormatter } from "./formatter";

const solarCalc = useSolarCalc();
const [formatNumber] = useFormatter();

/**
 * Calculate CO2 and trees
 * @param {Total solar} solarTotal
 */
export const calculateCO2_TREES = (solarTotal) => {
	const co2 = Math.round(parseFloat(solarTotal) * 0.505);
	const trees = Math.round(co2 / 22.5);
	return {
		co2,
		trees,
	};
};

/**
 * Format country search term for each site
 * @param {Country of site} country
 */
export const formatCountry = (country) => {
	switch (country) {
		case "VN":
			return "VN_VIETNAM";
		case "KH":
			return "KH_CAMBODIA";
		case "PH":
			return "PH_PHILIPPINES";
		default:
			return "THAILAND";
	}
};

/**
 * Validate live data of sites and format accordingly
 * @param {Live data of sites} liveData
 */
export const validateLiveData = (liveData) => {
	const today = moment();
	const liveDate = moment(liveData.dateData);
	const diff = today.diff(liveDate, "minutes");
	const result = {
		solar_power:
			diff <= 22 && liveData.is_online == 1 ? liveData.solar_power : 0,
		irradiance: diff <= 22 && liveData.is_online == 1 ? liveData.irradiance : 0,
	};
	const tempSP = result.solar_power;
	const tempIrr = result.irradiance;

	if (formatNumber(tempSP) != formatNumber(tempIrr)) {
		if (formatNumber(tempSP) == 0) {
			result.redHighlight = "solar_power";
		} else {
			if (formatNumber(tempIrr) == 0) {
				result.redHighlight = "irradiance";
			} else {
				result.redHighlight = "";
			}
		}
	} else {
		result.redHighlight = "";
	}
	return result;
};

const formatSiteData = (siteData, user, callback) => {
	if (siteData.length != 0) {
		const resLive = siteData[0];
		const resMonthly = siteData[1];
		const resYtd = siteData[2];
		const resToday = siteData[3];
		const resPrice = siteData[5].data.error == null ? siteData[5].data : [];
		const resCCTVIRTempAlarm = siteData[6]?.data;

		const liveData = resLive.data;
		const monthlyData = resMonthly.data;
		const ytdData = resYtd.data;
		let todayData = resToday.data;

		const priceData = resPrice.map((price) => ({
			table_name: price.table_name,
			price_off: price.price_off,
			discount_off: price.OFF_discount,
			price_on: price.price_on,
			discount_on: price.ON_discount,
			FT_cost: price.FT_cost,
			FT_discount: price.FT_discount,
			currency: price.currency,
		}));

		let resArr = [];

		if (priceData.length > 0) {
			todayData = todayData.map((row) => {
				if (moment().isBefore(moment(row.cod)) && row.client_id !== 27)
					return { ...row, isTestSite: true };
				else return { ...row, isTestSite: false };
			});
		}

		const clientType = ["client", "client_multi"];

		if (clientType.includes(user.type)) {
			todayData = todayData.map((row) => {
				const currency_unit = row?.country === "TH" ? "THB" : "VND";

				for (let i = 0; i < priceData.length; i++) {
					const price = priceData[i];

					let res = {};
					if (row.table_name === price.table_name) {
						res = { ...row, ...price };
					} else {
						const price_object = {
							table_name: row.table_name,
							price_off: 0,
							discount_off: 0,
							price_on: 0,
							discount_on: 0,
							FT_cost: 0,
							FT_discount: 0,
							currency: currency_unit,
						};
						res = { ...row, ...price_object };
					}

					return res;
				}
			});
		}

		liveData.map((live) => {
			let finalObj = {};
			const table_name = live.table_name;
			const formattedLive = validateLiveData(live);
			const overallData = [
				{ data: "monthly", arr: monthlyData },
				{ data: "ytd", arr: ytdData },
				{ data: "today", arr: todayData },
			];

			overallData.map((dataObj) => {
				const tempArr = dataObj.arr;
				const tempData = dataObj.data;

				for (let i = 0; i < tempArr.length; i++) {
					const valObj = tempArr[i];
					const isValid = typeof valObj !== "undefined";

					if (isValid && valObj.table_name === table_name) {
						let objReq = {};
						switch (tempData) {
							case "monthly":
								objReq = {
									theoretical_PR: valObj.theoretical_PR,
									brut_PR: valObj.brut_PR,
									client_id: valObj.client_id,
								};
								break;

							case "ytd":
								objReq = {
									brut_PR_YTD: valObj.brut_PR,
								};
								break;

							case "today":
								const prod = {
									ON: valObj.solar_ON,
									OFF: valObj.solar_OFF,
									TOTAL: valObj.solar_total,
								};
								const price = {
									ON: valObj.price_on,
									OFF: valObj.price_off,
									FT: valObj.FT_cost,
								};
								const discount = {
									ON: valObj.discount_on,
									OFF: valObj.discount_off,
									FT: valObj.FT_discount,
								};
								const savingsObj = solarCalc.calculateSavings(
									prod,
									price,
									discount,
									valObj.currency
								);

								const saving = savingsObj.savingsTotal;
								const totalEnergy = valObj.solar_total + valObj.grid_total;
								const totalON = valObj.solar_ON + valObj.grid_ON;
								const totalOFF = valObj.solar_OFF + valObj.grid_OFF;

								objReq = {
									power: valObj.power,
									solar_power: valObj.solar_power,
									country: formatCountry(valObj.country),
									co2: calculateCO2_TREES(valObj.solar_total).co2,
									trees: calculateCO2_TREES(valObj.solar_total).trees,
									grid_OFF: valObj.grid_OFF,
									grid_ON: valObj.grid_ON,
									grid_total: valObj.grid_total,
									solar_OFF: valObj.solar_OFF,
									solar_ON: valObj.solar_ON,
									solar_total: valObj.solar_total,
									totalEnergy,
									totalON,
									totalOFF,
									saving,
									isTestSite: valObj.isTestSite,
								};
								break;

							default:
								break;
						}

						finalObj = {
							...finalObj,
							...objReq,
						};

						break;
					}
				}
			});

			// add CCTV IR Temp Alarm data
			var alarmData = null;
			if (resCCTVIRTempAlarm && table_name in resCCTVIRTempAlarm) {
				alarmData = resCCTVIRTempAlarm[table_name];
			}

			let resObj = {
				site_id: live.site_id,
				company: todayData.find(data => data?.table_name === live.table_name)?.company, // get the name of the company
				status_name: live.status_name,
				status_color: live.status_color,
				overview_status: live.overview_status,
				site_name: live.site_name,
				power: finalObj.power != null ? finalObj.power : 0,
				solar_power: finalObj.solar_power != null ? finalObj.solar_power : 0,
				prYesterday: finalObj.brut_PR_YTD != null ? finalObj.brut_PR_YTD : 0,
				theoretical_PR:
					finalObj.theoretical_PR != null ? finalObj.theoretical_PR : 0,
				brut_PR: finalObj.brut_PR != null ? finalObj.brut_PR : 0,
				co2: finalObj.co2 != null ? finalObj.co2 : 0,
				trees: finalObj.trees != null ? finalObj.trees : 0,
				grid_OFF: finalObj.grid_OFF != null ? finalObj.grid_OFF : 0,
				grid_ON: finalObj.grid_ON != null ? finalObj.grid_ON : 0,
				grid_total: finalObj.grid_total != null ? finalObj.grid_total : 0,
				solar_OFF: finalObj.solar_OFF != null ? finalObj.solar_OFF : 0,
				solar_ON: finalObj.solar_ON != null ? finalObj.solar_ON : 0,
				solar_total: finalObj.solar_total != null ? finalObj.solar_total : 0,
				totalEnergy: finalObj.totalEnergy != null ? finalObj.totalEnergy : 0,
				totalON: finalObj.totalON != null ? finalObj.totalON : 0,
				totalOFF: finalObj.totalOFF != null ? finalObj.totalOFF : 0,
				saving: finalObj.saving != null ? finalObj.saving : 0,
				solar_power: formatNumber(formattedLive.solar_power),
				irr: formatNumber(formattedLive.irradiance),
				redHighlight: formattedLive.redHighlight,
				isTestSite: finalObj.isTestSite,
				clientID: finalObj.client_id,
				country: finalObj.country,
				alarmData,
			};

			resArr.push(resObj);
		});

		callback(resArr);
	}
};

export default formatSiteData;
